import { ReactElement } from 'react';
import { Document } from '@contentful/rich-text-types';
import RichText from 'components/UI/Richtext';
import CTA from './Cta';
import { IFragmentCtaFields } from 'types/contentful';
import dynamic from 'next/dynamic';

const Slider = dynamic(() => import('react-slick'), {
  ssr: false,
});

interface CarouselProps {
  titleElement?: ReactElement;
  title: string;
  description?: Document | undefined;
  items: ReactElement[];
  aspectRatio?: string;
  style?: string;
  descriptionImage?: string;
  cta?: IFragmentCtaFields | undefined;
  className?: string;
  innerClassName?: string;
  slidesToShow?: number;
  contentClassName?: string;
  sliderClassName?: string;
  tabletSlidesToShow?: number;
  mobileSlidesToShow?: number;
  titleClassName?: string;
}

export default function Carousel({
  titleElement = undefined,
  title = '',
  description = undefined,
  items,
  style = '',
  cta = undefined,
  className = '',
  innerClassName = '',
  slidesToShow = 3,
  contentClassName = '',
  sliderClassName = '',
  tabletSlidesToShow = 2,
  mobileSlidesToShow = 1,
  titleClassName = '',
}: CarouselProps): ReactElement {
  const settings = {
    dots: false,
    arrows: true,
    infinite: false,
    speed: 500,
    slidesToShow,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: tabletSlidesToShow,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: mobileSlidesToShow,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <div className={`${className}`}>
      <div className={`text-primary-actual ${innerClassName}`}>
        {!titleElement ? (
          <h3
            className={`${titleClassName} px-10 md:px-24 text-center text-new-34 md:text-4xl teal-fontcolor`}
          >
            {title}
          </h3>
        ) : (
          titleElement
        )}

        <div className={`w-2/3 mx-auto ${slidesToShow > 3 ? 'lg:w-full' : ''} ${contentClassName}`}>
          {description && (
            <div
              className={`text-center mt-2 text-lg max-w-xl mx-auto ${
                style === 'frames' ? 'hidden md:block' : ''
              }`}
            >
              <RichText document={description} classNames="text-lg text-primary-actual" />
            </div>
          )}
          {style === 'carousel-reviews' && (
            <div className="w-1/2 sm:w-1/3 md:w-1/4 mx-auto mt-5 mb-3">
              <img src="/images/star.png" alt="Star" className="mx-auto mw-100" />
            </div>
          )}
          {items && items.length ? (
            <Slider className={`mt-4 ${sliderClassName}`} {...settings}>
              {items.map((item, index) => (
                <div key={index}>{item}</div>
              ))}
            </Slider>
          ) : null}

          {description && (
            <div
              className={`w-4/5 mx-auto text-center mt-1 text-lg ${
                style === 'frames' ? 'block w-full mt-3 md:hidden' : 'hidden'
              }`}
            >
              <RichText
                document={description}
                classNames="text-lg text-primary-actual md:text-2xl"
              />
            </div>
          )}
        </div>
        {cta && (
          <div className={`text-center mt-16 lg:mt-20`}>
            {cta && <CTA {...cta} classNames="text-lg font-normal md:!w-full" />}
          </div>
        )}
      </div>
    </div>
  );
}
